import React, { useState, useEffect } from 'react';
import { routingAPI } from '../../services/api';
import Table from '../Common/Table';
import Modal from '../Common/Modal';

const emptyForm = () => ({
  name: '',
  address_line1: '',
  city: '',
  state: '',
  zip_code: '',
  country: 'USA',
  latitude: '',
  longitude: '',
  is_default: false,
  is_active: true,
});

const DepotsManagement = () => {
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDepot, setEditingDepot] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    loadDepots();
  }, []);

  const loadDepots = async () => {
    try {
      setLoading(true);
      const data = await routingAPI.listDepots({ include_inactive: '1' });
      setDepots(data);
    } catch (error) {
      alert('Error loading depots: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const lat = formData.latitude !== '' ? parseFloat(formData.latitude) : null;
    const lng = formData.longitude !== '' ? parseFloat(formData.longitude) : null;
    return {
      name: formData.name.trim(),
      address_line1: formData.address_line1.trim() || null,
      city: formData.city.trim() || null,
      state: formData.state.trim() || null,
      zip_code: formData.zip_code.trim() || null,
      country: formData.country.trim() || 'USA',
      latitude: lat != null && !Number.isNaN(lat) ? lat : null,
      longitude: lng != null && !Number.isNaN(lng) ? lng : null,
      is_default: !!formData.is_default,
      is_active: !!formData.is_active,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = buildPayload();
      if (editingDepot) {
        await routingAPI.updateDepot(editingDepot.id, payload);
      } else {
        await routingAPI.createDepot(payload);
      }
      setShowModal(false);
      setEditingDepot(null);
      resetForm();
      loadDepots();
    } catch (error) {
      alert('Error saving depot: ' + error.message);
    }
  };

  const handleEdit = (depot) => {
    setEditingDepot(depot);
    setFormData({
      name: depot.name || '',
      address_line1: depot.address_line1 || '',
      city: depot.city || '',
      state: depot.state || '',
      zip_code: depot.zip_code || '',
      country: depot.country || 'USA',
      latitude: depot.latitude != null ? String(depot.latitude) : '',
      longitude: depot.longitude != null ? String(depot.longitude) : '',
      is_default: !!depot.is_default,
      is_active: depot.is_active !== undefined ? !!depot.is_active : true,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        'Deactivate this depot? It will be hidden from route planning unless reactivated.'
      )
    ) {
      return;
    }
    try {
      await routingAPI.deleteDepot(id);
      loadDepots();
    } catch (error) {
      alert('Error deactivating depot: ' + error.message);
    }
  };

  const resetForm = () => setFormData(emptyForm());

  const formatCoords = (row) => {
    if (row.latitude == null || row.longitude == null) return '—';
    return `${Number(row.latitude).toFixed(4)}, ${Number(row.longitude).toFixed(4)}`;
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Name' },
    {
      key: 'address_line1',
      label: 'Address',
      format: (_, row) =>
        [row.address_line1, row.city, row.state, row.zip_code]
          .filter(Boolean)
          .join(', ') || '—',
    },
    { key: 'latitude', label: 'Coordinates', format: (_, row) => formatCoords(row) },
    {
      key: 'is_default',
      label: 'Default',
      format: (val) => (val ? 'Yes' : ''),
    },
    {
      key: 'is_active',
      label: 'Active',
      format: (val) => (val ? 'Yes' : 'No'),
    },
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Depots</h1>
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setEditingDepot(null);
            resetForm();
            setShowModal(true);
          }}
        >
          Add depot
        </button>
      </div>

      <p style={{ marginBottom: '1rem', color: '#555', fontSize: '0.95rem' }}>
        Route plans start and end at a depot. Leave latitude/longitude blank on create to
        geocode from the address automatically.
      </p>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table
          data={depots}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <Modal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingDepot(null);
          resetForm();
        }}
        title={editingDepot ? 'Edit depot' : 'Add depot'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Address line 1</label>
            <input
              type="text"
              value={formData.address_line1}
              onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Zip</label>
              <input
                type="text"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Country</label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Latitude (optional)</label>
              <input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                placeholder="Auto from address if empty"
              />
            </div>
            <div className="form-group">
              <label>Longitude (optional)</label>
              <input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) =>
                  setFormData({ ...formData, is_default: e.target.checked })
                }
              />
              Default depot (used when creating new route plans)
            </label>
          </div>
          {editingDepot && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
                Active
              </label>
            </div>
          )}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowModal(false);
                setEditingDepot(null);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Save
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DepotsManagement;
